import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";

const Footer = () => {
  const { t } = useTranslation();
  const footerRef = useRef<HTMLElement>(null);

  const { data: contactInfo } = useQuery({
    queryKey: ["contact-info"],
    queryFn: async () => {
      const { data, error } = await supabase.from("contact_info").select("*").limit(1).maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  const { data: hasProjects } = useQuery({
    queryKey: ["nav-has-projects"],
    queryFn: async () => {
      const { data, error } = await supabase.from("projects").select("id").limit(1);
      if (error) throw error;
      return data.length > 0;
    },
  });

  const { data: hasWood } = useQuery({
    queryKey: ["nav-has-wood"],
    queryFn: async () => {
      const { data, error } = await supabase.from("wood_items").select("id").limit(1);
      if (error) throw error;
      return data.length > 0;
    },
  });

  useEffect(() => {
    const el = footerRef.current;
    if (!el) return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      el.classList.add("is-revealed");
      return;
    }
    if (typeof IntersectionObserver === "undefined") {
      el.classList.add("is-revealed");
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            el.classList.add("is-revealed");
            io.disconnect();
          }
        });
      },
      { threshold: 0.15 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const phone = contactInfo?.phone || "+420 123 456 789";
  const phoneRaw = phone.replace(/\s/g, "");
  const email = contactInfo?.email || "info@umelecke-tesarstvi.cz";
  const address = contactInfo?.address || "Tesařská 123, Praha";

  return (
    <footer ref={footerRef} className="footer-curtain footer-gradient py-12">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div>
            <h3 className="font-serif text-lg font-bold mb-4">Umělecké Tesařství</h3>
            <p className="text-sm opacity-80">
              {t("footer.tagline", "Rekonstrukce starobylých památek s láskou k řemeslu a úctou k tradici.")}
            </p>
          </div>
          <nav aria-label={t("footer.navigation", "Navigace")}>
            <h4 className="font-serif text-lg font-bold mb-4">{t("footer.navigation", "Navigace")}</h4>
            <div className="flex flex-col gap-2">
              <Link to="/" className="text-sm footer-link">{t("nav.home")}</Link>
              {hasProjects && <Link to="/projekty" className="text-sm footer-link">{t("nav.projects")}</Link>}
              <Link to="/galerie" className="text-sm footer-link">{t("nav.gallery")}</Link>
              {hasWood && <Link to="/archivni-drevo" className="text-sm footer-link">{t("nav.wood")}</Link>}
              <Link to="/kontakt" className="text-sm footer-link">{t("nav.contact")}</Link>
            </div>
          </nav>
          <div>
            <h4 className="font-serif text-lg font-bold mb-4">{t("nav.contact")}</h4>
            <address className="text-sm opacity-80 space-y-1 not-italic">
              <p>{t("footer.phone_label", "tel")}: <a href={`tel:${phoneRaw}`} className="footer-link hover:opacity-100 transition-opacity">{phone}</a></p>
              <p>{t("footer.email_label", "email")}: <a href={`mailto:${email}`} className="footer-link hover:opacity-100 transition-opacity">{email}</a></p>
              <p>{address}</p>
            </address>
          </div>
        </div>
        <div className="mt-8 pt-8 border-t footer-divider flex flex-col gap-4">
          <nav aria-label="Právní informace" className="flex flex-wrap justify-center md:justify-start gap-x-6 gap-y-2 text-sm">
            <Link to="/obchodni-podminky" className="footer-link">Obchodní podmínky</Link>
            <Link to="/zasady-ochrany-osobnich-udaju" className="footer-link">Ochrana osobních údajů</Link>
            <Link to="/cookies" className="footer-link">Cookies</Link>
            <Link to="/pravni-upozorneni" className="footer-link">Právní upozornění</Link>
          </nav>
          <div className="flex flex-col md:flex-row justify-between items-center gap-2">
            <p className="text-sm footer-muted">© {new Date().getFullYear()} Umělecké tesařství s.r.o., IČO 24999881. {t("footer.rights")}.</p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
